<template>
  <v-dialog v-model="isVisible" max-width="540" data-cy="bulk-push-preview-dialog">
    <v-card>
      <v-card-title class="text-lg font-bold">Submit Liens to QuickBooks</v-card-title>
      <v-card-text>
        <p class="text-sm text-gray-600 mb-3">
          <template v-if="items.length === 1">
            A QuickBooks trust check will be created for the following finalized lien.
          </template>
          <template v-else>
            A QuickBooks trust check will be created for each of the following
            {{ pluralizeWithCount(items.length, 'finalized lien') }}.
          </template>
        </p>
        <div class="border border-gray-200 rounded-lg divide-y divide-gray-100 max-h-[280px] overflow-y-auto">
          <div
            v-for="lien in items"
            :key="lien.lienId"
            class="flex items-center justify-between px-4 py-2.5"
          >
            <div class="min-w-0">
              <div class="font-semibold text-gray-900 truncate">
                {{ lien.vendorDisplayName }}
              </div>
              <div class="text-xs text-gray-500">{{ lien.lienTypeDisplayName }}</div>
            </div>
            <div class="font-bold tabular-nums text-gray-900 pl-3">
              {{ formatCurrency(lien.currentAmount) }}
            </div>
          </div>
        </div>
        <div
          class="flex items-center justify-between px-4 py-3 mt-3 bg-gray-50 border border-gray-200 rounded-lg font-bold"
        >
          <span>Total</span>
          <span class="tabular-nums">{{ formatCurrency(total) }}</span>
        </div>
      </v-card-text>
      <v-card-actions class="pa-6">
        <LlSecondaryButton data-cy="bulk-push-cancel" @click="cancel">Cancel</LlSecondaryButton>
        <v-spacer />
        <LlSecondaryButton variant="outlined" color="primary" data-cy="bulk-push-confirm" @click="confirm">
          <QboIcon :size="16" class="mr-2" />
          Submit {{ pluralizeWithCount(items.length, 'lien') }} to QuickBooks
        </LlSecondaryButton>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script setup lang="ts">
import { ref, computed } from 'vue';
import LlSecondaryButton from '@/components/LlSecondaryButton.vue';
import type { LienData } from '@/models/lienTypes';
import { formatCurrency, pluralizeWithCount } from '@/services/utils';
import QboIcon from '@/components/QboIcon.vue';

const isVisible = ref(false);
const items = ref<LienData[]>([]);
let resolvePromise: ((value: boolean) => void) | null = null;

const total = computed(() =>
  items.value.reduce((sum, l) => sum + (l.currentAmount ?? 0), 0),
);

/** Open the preview for the given liens; resolves true when confirmed. */
function open(liens: LienData[]): Promise<boolean> {
  items.value = liens;
  isVisible.value = true;
  return new Promise((resolve) => {
    resolvePromise = resolve;
  });
}

function confirm(): void {
  isVisible.value = false;
  resolvePromise?.(true);
  resolvePromise = null;
}

function cancel(): void {
  isVisible.value = false;
  resolvePromise?.(false);
  resolvePromise = null;
}

defineExpose({ open });
</script>
